import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  computed,
  effect,
  inject,
  signal,
  untracked,
} from '@angular/core';
import { DFPService } from '@services/http/dfp/dfp.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { catchError, filter, first, map, of, take } from 'rxjs';
import { DialogService } from 'primeng/dynamicdialog';
import { VaultBaseComponent } from '@shared/components/base/vault-base.component';
import { SharedModule } from '@shared/shared.module';
import { openFilePreviewDialog } from '@shared/utils/file-preview-dialog.util';
import { AppState } from '@src/app/state';
import { selectCompanyId } from '@src/app/state/auth/auth.selectors';
import { CompanyService } from '@app/core/services/http/company.service';
import { ConfirmationService } from '@app/core/services/helpers/confirmation.service';
import { ToastHelperService } from '@app/core/services/helpers/toast-helper.service';
import { Option } from '@interfaces/common/option';
import {
  buildLocalCompanyQueryParam,
  parseAnyCompanyQueryParam,
} from '@shared/components/select-company/company-query-param.util';
import { PeriodCoverageResponse } from '@interfaces/accounting-document-management/responses/document-coverage.response';
import { CoverageSummaryComponent } from '@features/dashboard/accounting-document-management/components/coverage-summary/coverage-summary.component';
import { PeriodFilesPanelComponent } from '@features/dashboard/accounting-document-management/components/period-files-panel/period-files-panel.component';
import { YearCoverageComponent } from '@features/dashboard/accounting-document-management/components/year-coverage/year-coverage.component';
import { AccountingDocumentManagementStateService } from '@features/dashboard/accounting-document-management/services/accounting-document-management-state.service';
import { PendencyChip } from '@features/dashboard/accounting-document-management/utils/coverage-view.util';
import { COVERAGE_CARD_CONTENT } from './accounting-document-management.constants';

/**
 * Página orquestradora da cobertura documental (`/dashboard/accounting-document-management`, PBI
 * #2728): resolve a empresa em escopo e liga o estado do
 * {@link AccountingDocumentManagementStateService} aos componentes de apresentação da tela unificada
 * (`app-coverage-summary` + `app-year-coverage` — ver `README.md` da feature para o histórico das duas
 * abas que existiam antes desta unificação). `app-period-files-panel` não é projetado no corpo do
 * template: vive dentro de um `<ng-template>` (`selectionSlot`) que a própria página declara e passa a
 * `app-year-coverage` via `selectionTemplate` — é `year-coverage` quem decide em qual trilha
 * instanciá-lo (`ngTemplateOutlet`), ancorado no período selecionado. Isso é a **inspeção inline**:
 * clicar numa célula abre, dentro da própria trilha, a lista de documentos daquele período (qual é o
 * considerado, quais foram substituídos) — e **nada mais**. Selecionar não envia: desde 21/08/2026 o
 * único caminho de envio da tela é a área de arraste do exercício (`fileDroppedOnYear`), em que a Cog
 * AI identifica período e trilha pelo conteúdo do arquivo.
 *
 * Duas responsabilidades exclusivas dela, que nenhum componente sabe resolver por si:
 *
 * 1. **Abrir arquivo**: `year-coverage` só emite `filePreviewRequested` — é esta página que busca o
 *    link ({@link AccountingDocumentManagementStateService.getDownloadLink}) e abre
 *    `FilePreviewModalComponent`.
 * 2. **Remover arquivo**: `onFileRemoved` confirma via `ConfirmationService` antes de
 *    `stateService.deleteFile()` — `period-files-panel` só emite `fileRemoved`.
 */
@Component({
  selector: 'app-accounting-document-management',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    SharedModule,
    CoverageSummaryComponent,
    YearCoverageComponent,
    PeriodFilesPanelComponent,
  ],
  providers: [AccountingDocumentManagementStateService],
  templateUrl: './accounting-document-management.component.html',
  styleUrl: './accounting-document-management.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccountingDocumentManagementComponent extends VaultBaseComponent implements OnInit {
  protected readonly stateService = inject(AccountingDocumentManagementStateService);
  protected override readonly store = inject(Store<AppState>);
  private readonly destroyRef = inject(DestroyRef);
  private readonly companyService = inject(CompanyService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly dialogService = inject(DialogService);
  private readonly toast = inject(ToastHelperService);
  private readonly dfpService = inject(DFPService);

  protected readonly companyId = signal('');
  protected readonly companyOptions = signal<Option[]>([]);
  protected readonly isLoadingCompanies = signal(false);
  protected readonly companyControl = new FormControl<string | null>(null);

  /** Cabeçalho fixo do card — uma tela só, sem aba (ver `accounting-document-management.constants.ts`). */
  protected readonly cardContent = COVERAGE_CARD_CONTENT;

  /**
   * O `PeriodCoverageResponse` do período selecionado, dentro de {@link AccountingDocumentManagementStateService.years}.
   * `null` sem seleção. Resolvido pela trilha (`documentType`) e pelo regime dela: no regime `"Mensal"`
   * (Faturamento) o período é sempre um dos 12 `months`; no regime `"Anual"` (DRE/Balanço) é o slot
   * anual (`(1, 12)`) ou, para qualquer outro intervalo (chip de semestre/trimestre/mês/YTD), o item
   * correspondente em `track.periods` — convertido para o formato de `PeriodCoverageResponse` que o
   * painel de arquivos do período espera.
   */
  protected readonly selectedPeriodCoverage = computed<PeriodCoverageResponse | null>(() => {
    const selected = this.stateService.selectedPeriod();
    if (!selected) return null;

    const track = this.stateService
      .years()
      .find(year => year.year === selected.year)
      ?.tracks.find(candidate => candidate.documentType === selected.documentType);
    if (!track) return null;

    if (track.requirementMode === 'Mensal') {
      return track.months[selected.startMonth - 1] ?? null;
    }

    if (this.selectedPeriodIsAnnual()) return track.annual;

    const entry = track.periods.find(
      period => period.startMonth === selected.startMonth && period.endMonth === selected.endMonth
    );
    if (entry) {
      return {
        statusId: entry.statusId,
        year: entry.year,
        month: entry.endMonth,
        isRequired: false,
        status: entry.status,
        notes: entry.notes,
        files: entry.files,
      };
    }

    return track.months.find(month => month.month === selected.endMonth) ?? null;
  });

  /** `true` quando o período selecionado é o slot anual `(1, 12)` de uma trilha — DRE/Balanço, nunca Faturamento. */
  protected readonly selectedPeriodIsAnnual = computed(() => {
    const selected = this.stateService.selectedPeriod();
    return selected !== null && selected.startMonth === 1 && selected.endMonth === 12;
  });

  /** O período selecionado é da trilha de Faturamento (card 3700): o aviso de duplicidade mostra a regra do backend. */
  protected readonly selectedPeriodIsRevenue = computed(
    () => this.stateService.selectedPeriod()?.documentType === 'Faturamento'
  );

  /** Regra escrita do Faturamento (`ruleText`), carregada uma vez por empresa no primeiro mês de Faturamento aberto. */
  protected readonly revenueRuleText = signal<string | null>(null);
  private revenueRuleCompanyId: string | null = null;

  private readonly revenueRuleLoader = effect(() => {
    const companyId = this.companyId();
    const selected = this.stateService.selectedPeriod();
    if (!companyId || selected?.documentType !== 'Faturamento') return;
    untracked(() => this.loadRevenueRuleText(companyId, selected.year));
  });

  private loadRevenueRuleText(companyId: string, year: number): void {
    if (this.revenueRuleCompanyId === companyId) return;
    this.revenueRuleCompanyId = companyId;
    this.revenueRuleText.set(null);
    this.dfpService
      .getRevenueStatementOrigin(companyId, year)
      .pipe(
        catchError(() => of(null)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(origin => {
        if (this.revenueRuleCompanyId !== companyId) return;
        this.revenueRuleText.set(origin?.ruleText?.trim() || null);
        // Falhou: tenta de novo no próximo mês aberto.
        if (!origin) this.revenueRuleCompanyId = null;
      });
  }

  /**
   * Gate de elegibilidade de envio (PBI #2728, 24/08/2026), repassado a `app-year-coverage`. `?? true`
   * de propósito: antes do gate responder (`uploadEligibility()` ainda `null`), a tela assume liberado
   * em vez de piscar o aviso de bloqueio para a maioria das empresas, que já tem onboarding — o custo
   * de errar otimista por uma fração de segundo é menor que o de mostrar bloqueio falso.
   */
  protected readonly canUploadDocuments = computed(
    () => this.stateService.uploadEligibility()?.canUpload ?? true
  );

  /** Motivo do bloqueio pronto para exibição, repassado a `app-year-coverage`. */
  protected readonly uploadBlockReason = computed(
    () => this.stateService.uploadEligibility()?.blockReason ?? null
  );

  ngOnInit(): void {
    // O seletor de empresa do cabeçalho é o **único** caminho de troca de empresa: a empresa do escopo
    // (NgRx) ou da URL só pré-seleciona o controle, e é o `valueChanges` que dispara a carga. Assim
    // trocar de empresa na tela, chegar nela com escopo já definido e abrir um link compartilhado
    // percorrem exatamente o mesmo código.
    this.companyControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef), filter(Boolean))
      .subscribe(id => this.initCompany(id));

    this.loadCompanyOptions();
  }

  private initCompany(id: string): void {
    this.companyId.set(id);
    this.syncCompanyIdToUrl(id);
    this.stateService.init(id);
    this.stateService.loadCoverage();
    this.stateService.loadUploadEligibility();
  }

  /**
   * Reflete a empresa selecionada na URL (`?companyId=<id>`), no mesmo padrão das outras telas com
   * seletor local de empresa (`buildLocalCompanyQueryParam`, que ainda limpa os aliases legados
   * `companyIds`/`COMPANYID`). `merge` preserva qualquer outro param da rota — inclusive um `tab`
   * legado de link antigo (`?tab=demonstracoes`), que a tela unificada simplesmente ignora e não
   * reescreve; `replaceUrl` evita empilhar uma entrada de histórico por troca de empresa — o Voltar do
   * navegador continua saindo da tela, não desfazendo seleção por seleção.
   */
  private syncCompanyIdToUrl(id: string): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: buildLocalCompanyQueryParam([id]),
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  private loadCompanyOptions(): void {
    this.isLoadingCompanies.set(true);
    this.companyService
      .getAllCompanys()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: res => {
          const options = (res?.data ?? [])
            .filter(c => c.id)
            .map(c => ({ label: c.displayName || c.corporateName, value: c.id as string }));
          this.companyOptions.set(options);
          this.isLoadingCompanies.set(false);
          this.preselectCompany(options);
        },
        error: () => this.isLoadingCompanies.set(false),
      });
  }

  /**
   * Pré-seleciona a empresa no seletor, só depois que as opções chegaram — um valor fora da lista
   * deixaria o `app-select` sem rótulo. Duas fontes, nesta ordem (a prioridade obrigatória da seção 16
   * do `AGENTS.md`, "Seletor Local de Empresa com Escopo Global"):
   *
   * 1. **URL** (`?companyId=`): um link compartilhado não pode abrir na empresa da sessão de quem
   *    clicou. Também é o que faz F5 manter a empresa.
   * 2. **Escopo do login** (NgRx `selectCompanyId`): só quando a URL não diz nada, para não trocar o
   *    contexto do usuário sem pedir.
   *
   * Sem nenhuma das duas (escopo em "todas as empresas", ou empresa fora da lista), a tela abre no
   * estado vazio pedindo a seleção — comportamento intencional e inalterado.
   */
  private preselectCompany(options: Option[]): void {
    const fromUrl = this.companyIdFromUrl();
    if (fromUrl && options.some(option => option.value === fromUrl)) {
      this.companyControl.setValue(fromUrl);
      return;
    }

    this.store
      .select(selectCompanyId)
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe(id => {
        if (id && options.some(option => option.value === id)) {
          this.companyControl.setValue(id);
        }
      });
  }

  /**
   * Empresa do `?companyId=` da URL, quando ela expressa **uma** empresa.
   *
   * Lê com `parseAnyCompanyQueryParam` (aceita os aliases legados) e ignora de propósito o caso
   * multiempresa: `companyId=a,b` é o formato do **filtro** das telas multiempresa (Padronização, Comex,
   * Kanban de crédito), e o `app-top-menu` propaga esse valor para os links de menu. Esta tela é de uma
   * empresa por vez, então escolher arbitrariamente a primeira de uma lista de três seria inventar uma
   * intenção que o usuário não teve — nesse caso vale o escopo do login.
   */
  private companyIdFromUrl(): string | null {
    const ids = parseAnyCompanyQueryParam(this.route.snapshot.queryParams);
    return ids.length === 1 ? ids[0] : null;
  }

  /** Chip de pendência clicado: o chip já carrega o período completo a selecionar. */
  protected onChipSelected(chip: PendencyChip): void {
    this.stateService.selectPeriod({
      year: chip.year,
      documentType: chip.documentType,
      startMonth: chip.startMonth,
      endMonth: chip.endMonth,
    });
  }

  /**
   * Arquivo solto na área de envio de um exercício da régua: vai sem período, e a Cog AI decide
   * exercício/intervalo/trilha. O ano do card vira **dica**, não verdade — se a IA discordar, o
   * documento é arquivado pelo que está escrito nele.
   */
  protected onClassifiedFilesDropped(event: { year: number; files: readonly File[] }): void {
    this.stateService.sendClassifiedFiles(event.files, event.year);
  }

  /**
   * Confirma antes de excluir, **nomeando o que sai junto**. O texto anterior ("Confirma a remoção
   * definitiva deste arquivo?") prometia mais do que o backend fazia — era só soft delete da extração, com
   * valores, De/Para e projeção sobrevivendo. Desde 24/08/2026 a remoção é em cascata (ver
   * `AccountingDocumentManagementService.DeleteDocumentAsync`), e é justamente por ser ampla que a modal
   * precisa listar o alcance: quem clica no ✕ de um mês não imagina que está removendo números da Análise
   * Econômica e, no caso de documento vindo do onboarding, a cópia no Data Room.
   */
  protected onFileRemoved(extractionId: string): void {
    this.confirmationService
      .confirm(
        'Remover documento do sistema',
        'O arquivo sai desta tela, os valores extraídos dele saem da Análise Econômica e o vínculo ' +
          'De/Para é desfeito. Se o documento veio do onboarding, a submissão e a cópia no Data Room ' +
          'também são removidas. A cobertura dos períodos que ele fechava volta a ficar pendente. ' +
          'Deseja continuar?',
        'danger',
        'Cancelar',
        'Remover do sistema'
      )
      .pipe(first(), takeUntilDestroyed(this.destroyRef))
      .subscribe(confirmed => {
        if (confirmed) this.stateService.deleteFile(extractionId);
      });
  }

  /**
   * Busca o link e abre `FilePreviewModalComponent` — chamado pelo `filePreviewRequested` de `year-coverage`.
   *
   * O erro é traduzido por `describeError` em vez de virar sempre "Não foi possível abrir o arquivo.":
   * documento cuja submissão de onboarding foi removida depois da extração volta do backend com a causa
   * escrita ("o arquivo enviado no onboarding foi removido depois da extração"), e engolir isso num texto
   * genérico era o que deixava o usuário sem saber se o arquivo existia em algum lugar.
   */
  protected openFile(extractionId: string): void {
    const fallback = 'Não foi possível abrir o arquivo.';

    this.stateService
      .getDownloadLink(extractionId)
      .pipe(
        map(link => ({ link, error: null as string | null })),
        catchError((err: unknown) =>
          of({ link: null, error: this.stateService.describeError(err, fallback) })
        ),
        first(),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(({ link, error }) => {
        if (!link) {
          this.toast.showToastError(error ?? fallback);
          return;
        }
        openFilePreviewDialog(this.dialogService, link);
      });
  }

  /**
   * "Ir para o onboarding", clicado no aviso que substitui a área de envio quando o gate de
   * elegibilidade nega o upload (`onboardingRequested` de `app-year-coverage`).
   *
   * Vai sempre para a rota **da empresa** (`/dashboard/onboarding/:companyId`) — nunca para uma
   * listagem de onboardings — porque esta tela só chega a existir com uma empresa já selecionada
   * (`@if (!companyId())` no template esconde a cobertura inteira, aviso incluso, sem empresa). Não
   * existe rota de listagem de onboarding registrada em `dashboard-routing.module.ts` para usar como
   * alternativa; se um dia esta tela abrir sem empresa fixa, este método precisa ser revisto.
   *
   * `OnboardingV2Component` cria o `OnboardingProcess` sob demanda na primeira abertura
   * (`OnboardingService.GetOrCreateOnboardingProcessAsync`, ver README do backend) — então o clique
   * sempre resolve algo, mesmo para uma empresa que nunca abriu o onboarding.
   */
  protected goToOnboarding(): void {
    this.router.navigate(['/dashboard/onboarding', this.companyId()]);
  }
}
